// Copyright 2025, University of Maryland, All Rights Reserved
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui';
import { useAuth } from '../context/AuthContext';
import { ImpersonationMenuItem } from './ImpersonationMenuItem';
import { ChevronDown, User, LogOut } from 'lucide-react';

interface ProfileMenuProps {
  isGlobalAdmin: boolean;
  app_id: string;
}

export function ProfileMenu({
  isGlobalAdmin,
  app_id,
}: ProfileMenuProps) {
  const { user, userId, impersonatingUserId, logout } = useAuth();

  // When impersonating, show the impersonated user's ID, otherwise show the actual user's ID
  const displayUserId = impersonatingUserId || userId;

  // Get user's display name - prioritize display userId, then CAS fields
  const userName = displayUserId || user?.name || user?.username || user?.email || 'User';
  const userEmail = displayUserId
    ? (displayUserId.includes('@') ? displayUserId : `${displayUserId}@umd.edu`)
    : undefined;

  // Check if there are any visible menu items
  const hasVisibleItems = isGlobalAdmin || Boolean(logout);

  // If no visible items, just show the user name without dropdown
  if (!hasVisibleItems) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
          <User className="h-4 w-4" />
        </div>
        <span className="text-sm font-medium text-gray-700">{userName}</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
          <button data-testid="nav-profile-menu" className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-[background-color,transform] duration-150 hover:bg-slate-100 active:scale-[.98] data-[state=open]:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white">
              <User className="h-4 w-4 text-slate-500" />
            </div>
            <span className="text-sm font-medium">{userName}</span>
            <ChevronDown className="h-4 w-4 text-slate-500 transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} className="w-64 rounded-xl border-slate-200 p-2 shadow-xl">
          <DropdownMenuLabel data-testid="nav-profile-identity" className="flex items-center gap-3 px-2 py-2 font-normal">
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white">
              <User className="h-5 w-5 text-slate-500" />
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-900">{userName}</div>
              {userEmail && <div className="truncate text-sm text-slate-500">{userEmail}</div>}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="my-1" />
          {isGlobalAdmin && <ImpersonationMenuItem app_id={app_id} />}
          {logout && (
              <DropdownMenuItem variant="destructive" onClick={logout} data-testid="nav-menu-logout" className="px-2 py-2 text-sm">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
          )}
        </DropdownMenuContent>
    </DropdownMenu>
  );
}
